import { useState } from "react";
import { useLocation } from "react-router-dom";
import { trpc } from "../hooks/trpc";
import { useAuth } from "../hooks/useAuth";
import { KeyRound, CheckCircle2, AlertCircle, Shield, Mail, User, ShieldCheck, ShieldOff } from "lucide-react";
import { Eyebrow, LabelLine } from "../components/brand";

export function ProfilPage() {
  const { user } = useAuth();
  const location = useLocation();
  const forceChange = user?.forcePasswordChange || (location.state as any)?.forcePasswordChange;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MFA State
  const [mfaStep, setMfaStep] = useState<"idle" | "setup" | "verify" | "disable">("idle");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null);

  const changeMutation = trpc.auth.changePassword.useMutation();
  const setupMfaMutation = trpc.auth.setupMfa.useMutation();
  const enableMfaMutation = trpc.auth.enableMfa.useMutation();
  const disableMfaMutation = trpc.auth.disableMfa.useMutation();
  const meQuery = trpc.auth.me.useQuery(undefined, { retry: false });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword.length < 10 || !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/.test(newPassword)) {
      setError("Passwort muss mind. 10 Zeichen mit Gross-/Kleinbuchstaben und Ziffern enthalten");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwoerter stimmen nicht ueberein");
      return;
    }

    try {
      await changeMutation.mutateAsync({ currentPassword, newPassword });
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(err.message ?? "Passwort konnte nicht geaendert werden");
    }
  };

  const infoTile = (icon: React.ReactNode, label: string, value: string) => (
    <div className="flex items-center gap-3">
      <div
        className="w-11 h-11 flex items-center justify-center flex-shrink-0"
        style={{
          border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
          borderRadius: "var(--radius-sm)",
          color: "var(--color-primary)",
        }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div
          className="text-[10px]"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          {label}
        </div>
        <div
          className="text-sm font-medium truncate"
          style={{ color: "var(--color-foreground)", fontFamily: "var(--font-display)" }}
        >
          {value}
        </div>
      </div>
    </div>
  );

  return (
    <div className="animate-in">
      <div className="mb-10">
        <Eyebrow>Konto · Sicherheit</Eyebrow>
        <h1
          className="text-2xl font-bold mt-4"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
        >
          Profil
        </h1>
        <p
          className="text-xs mt-2"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          Kontoeinstellungen und Sicherheit
        </p>
      </div>

      {/* Profil-Info */}
      <div className="card p-6 mb-6">
        <LabelLine style={{ marginBottom: 20, display: "block" }}>Kontoinformationen</LabelLine>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
          {infoTile(<User size={16} strokeWidth={1.5} />, "Name", user?.name ?? user?.username ?? "—")}
          {infoTile(<Mail size={16} strokeWidth={1.5} />, "Email", user?.email ?? "—")}
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 flex items-center justify-center flex-shrink-0"
              style={{
                border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
                borderRadius: "var(--radius-sm)",
                color: "var(--color-success)",
              }}
            >
              <Shield size={16} strokeWidth={1.5} />
            </div>
            <div>
              <div
                className="text-[10px]"
                style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                Rolle
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: "var(--color-foreground)", fontFamily: "var(--font-display)" }}
              >
                {user?.roleName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 flex items-center justify-center flex-shrink-0"
              style={{
                border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
                borderRadius: "var(--radius-sm)",
                color: user?.totpEnabled ? "var(--color-success)" : "var(--color-warning)",
              }}
            >
              <KeyRound size={16} strokeWidth={1.5} />
            </div>
            <div>
              <div
                className="text-[10px]"
                style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                Zwei-Faktor
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: "var(--color-foreground)", fontFamily: "var(--font-display)" }}
              >
                {user?.totpEnabled ? "Aktiviert" : "Nicht aktiviert"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Passwort-Aenderung erforderlich */}
      {forceChange && !success && (
        <div
          className="flex items-center gap-3 p-4 text-sm mb-6 max-w-lg"
          style={{
            background: "var(--color-warning-bg)",
            color: "var(--color-warning)",
            border: "1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <AlertCircle size={18} strokeWidth={1.5} className="flex-shrink-0" />
          <div>
            <div className="font-semibold" style={{ fontFamily: "var(--font-display)" }}>
              Passwortaenderung erforderlich
            </div>
            <div className="text-xs mt-0.5 opacity-80" style={{ fontFamily: "var(--font-body)" }}>
              Dein Konto verwendet noch das Standard-Passwort. Bitte aendere es jetzt.
            </div>
          </div>
        </div>
      )}

      {/* Passwort aendern */}
      <div className="card p-4 md:p-6 max-w-lg">
        <LabelLine style={{ marginBottom: 20, display: "block" }}>Passwort aendern</LabelLine>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-5">
          <div>
            <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Aktuelles Passwort</LabelLine>
            <input
              type="password"
              className="input mt-2"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Aktuelles Passwort"
            />
          </div>
          <div>
            <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Neues Passwort</LabelLine>
            <input
              type="password"
              className="input mt-2"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Min. 10 Zeichen"
            />
          </div>
          <div>
            <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Neues Passwort bestaetigen</LabelLine>
            <input
              type="password"
              className="input mt-2"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
            />
          </div>

          {error && (
            <div
              className="flex items-center gap-2 p-3 text-xs"
              style={{ background: "var(--color-danger-bg)", color: "var(--color-danger)", borderRadius: "var(--radius-sm)" }}
            >
              <AlertCircle size={14} strokeWidth={1.5} /> {error}
            </div>
          )}

          {success && (
            <div
              className="flex items-center gap-2 p-3 text-xs"
              style={{ background: "var(--color-success-bg)", color: "var(--color-success)", borderRadius: "var(--radius-sm)" }}
            >
              <CheckCircle2 size={14} strokeWidth={1.5} /> Passwort erfolgreich geaendert
            </div>
          )}

          <button type="submit" disabled={changeMutation.isPending} className="btn btn-primary">
            {changeMutation.isPending ? (
              <div
                className="w-4 h-4 rounded-full animate-spin"
                style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white" }}
              />
            ) : (
              <><KeyRound size={14} strokeWidth={1.5} /> Passwort aendern</>
            )}
          </button>
        </form>
      </div>

      {/* Zwei-Faktor-Authentifizierung */}
      <div className="card p-6 max-w-lg mt-6">
        <LabelLine style={{ marginBottom: 20, display: "block" }}>Zwei-Faktor-Authentifizierung (TOTP)</LabelLine>

        {mfaError && (
          <div
            className="flex items-center gap-2 p-3 text-xs mb-4 mt-5"
            style={{ background: "var(--color-danger-bg)", color: "var(--color-danger)", borderRadius: "var(--radius-sm)" }}
          >
            <AlertCircle size={14} strokeWidth={1.5} /> {mfaError}
          </div>
        )}

        {mfaSuccess && (
          <div
            className="flex items-center gap-2 p-3 text-xs mb-4 mt-5"
            style={{ background: "var(--color-success-bg)", color: "var(--color-success)", borderRadius: "var(--radius-sm)" }}
          >
            <CheckCircle2 size={14} strokeWidth={1.5} /> {mfaSuccess}
          </div>
        )}

        {user?.totpEnabled && mfaStep === "idle" && (
          <div className="mt-5">
            <div
              className="flex items-center gap-3 mb-4 p-3"
              style={{ background: "var(--color-success-bg)", borderRadius: "var(--radius-sm)" }}
            >
              <ShieldCheck size={18} strokeWidth={1.5} style={{ color: "var(--color-success)" }} />
              <span
                className="text-sm font-medium"
                style={{ color: "var(--color-success)", fontFamily: "var(--font-display)" }}
              >
                2FA ist aktiviert
              </span>
            </div>
            <button
              onClick={() => {
                setMfaError(null);
                setMfaSuccess(null);
                setMfaCode("");
                setMfaStep("disable");
              }}
              className="btn btn-danger"
            >
              <ShieldOff size={14} strokeWidth={1.5} /> 2FA deaktivieren
            </button>
          </div>
        )}

        {user?.totpEnabled && mfaStep === "disable" && (
          <div className="mt-5">
            <p
              className="text-sm mb-3"
              style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}
            >
              Gib deinen aktuellen 2FA-Code ein, um die Zwei-Faktor-Authentifizierung zu deaktivieren.
            </p>
            <div className="flex gap-2 items-end flex-wrap">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-stelliger Code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                className="input w-40"
              />
              <button
                onClick={async () => {
                  setMfaError(null);
                  try {
                    await disableMfaMutation.mutateAsync({ code: mfaCode });
                    setMfaSuccess("2FA wurde deaktiviert");
                    setMfaStep("idle");
                    setMfaCode("");
                    meQuery.refetch();
                  } catch (err: any) {
                    setMfaError(err.message ?? "Fehler beim Deaktivieren");
                  }
                }}
                disabled={disableMfaMutation.isPending || mfaCode.length !== 6}
                className="btn btn-danger"
              >
                Bestaetigen
              </button>
              <button
                onClick={() => { setMfaStep("idle"); setMfaCode(""); setMfaError(null); }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {!user?.totpEnabled && mfaStep === "idle" && (
          <div className="mt-5">
            <p
              className="text-sm mb-4"
              style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}
            >
              Schuetze dein Konto mit einem Einmalpasswort (TOTP). Du brauchst eine Authenticator-App wie Google Authenticator, Authy oder 1Password.
            </p>
            <button
              onClick={async () => {
                setMfaError(null);
                setMfaSuccess(null);
                try {
                  const result = await setupMfaMutation.mutateAsync();
                  setQrCode(result.qrCode);
                  setMfaSecret(result.secret);
                  setMfaStep("setup");
                } catch (err: any) {
                  setMfaError(err.message ?? "Fehler beim Einrichten");
                }
              }}
              disabled={setupMfaMutation.isPending}
              className="btn btn-primary"
            >
              {setupMfaMutation.isPending ? (
                <div
                  className="w-4 h-4 rounded-full animate-spin"
                  style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white" }}
                />
              ) : (
                <><ShieldCheck size={14} strokeWidth={1.5} /> 2FA einrichten</>
              )}
            </button>
          </div>
        )}

        {mfaStep === "setup" && qrCode && (
          <div className="flex flex-col gap-5 mt-5">
            <div>
              <p
                className="text-sm mb-3"
                style={{ color: "var(--color-foreground)", fontFamily: "var(--font-body)" }}
              >
                1. Scanne den QR-Code mit deiner Authenticator-App:
              </p>
              <div
                className="flex justify-center p-4"
                style={{ background: "#FFFFFF", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
              >
                <img src={qrCode} alt="TOTP QR-Code" className="w-48 h-48" />
              </div>
            </div>
            <div>
              <p
                className="text-sm mb-2"
                style={{ color: "var(--color-foreground)", fontFamily: "var(--font-body)" }}
              >
                Oder gib diesen Code manuell ein:
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 p-2.5 text-xs select-all break-all"
                  style={{
                    background: "var(--color-bg-elevated)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-primary)",
                    fontFamily: "var(--font-mono)",
                    borderRadius: "var(--radius-sm)",
                  }}
                >
                  {mfaSecret}
                </code>
              </div>
            </div>
            <div>
              <p
                className="text-sm mb-2"
                style={{ color: "var(--color-foreground)", fontFamily: "var(--font-body)" }}
              >
                2. Gib den 6-stelligen Code aus der App ein:
              </p>
              <input
                type="text"
                className="input text-center text-xl tracking-[0.5em]"
                style={{ fontFamily: "var(--font-mono)" }}
                maxLength={6}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                autoFocus
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={async () => {
                  setMfaError(null);
                  if (mfaCode.length !== 6) {
                    setMfaError("Bitte 6-stelligen Code eingeben");
                    return;
                  }
                  try {
                    await enableMfaMutation.mutateAsync({ code: mfaCode });
                    setMfaSuccess("2FA wurde erfolgreich aktiviert");
                    setMfaStep("idle");
                    setQrCode(null);
                    setMfaSecret(null);
                    setMfaCode("");
                    meQuery.refetch();
                  } catch (err: any) {
                    setMfaError(err.message ?? "Code ungueltig");
                  }
                }}
                disabled={enableMfaMutation.isPending}
                className="btn btn-success"
              >
                {enableMfaMutation.isPending ? (
                  <div
                    className="w-4 h-4 rounded-full animate-spin"
                    style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white" }}
                  />
                ) : (
                  <><CheckCircle2 size={14} strokeWidth={1.5} /> Aktivieren</>
                )}
              </button>
              <button
                onClick={() => {
                  setMfaStep("idle");
                  setQrCode(null);
                  setMfaSecret(null);
                  setMfaCode("");
                  setMfaError(null);
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
